import { useEffect, useState } from "react";
import { iUser } from "../contexts/UserContext";
import Box from "@mui/material/Box/Box";
import Typography from "@mui/material/Typography/Typography";
import Container from '@mui/material/Container/Container';

import {
  Button,
  Grid,
  Link,
  Switch,
  List, ListItem,
  ListItemText,
  TextField
} from "@mui/material";
import userService from "../services/UserService";
import { MfaType } from "../utilities/FormEnums";
import { iUserDTO } from "../utilities/APIInterfaces";
import { enqueueSnackbar } from "notistack";
import ConfirmationDialog from "./ConfirmationDialog";
import { visuallyHidden } from '@mui/utils';

const actionUrl = `${process.env.REACT_APP_KEYCLOAK_URL}/`
  + `realms/${process.env.REACT_APP_KEYCLOAK_REALM}/`
  + `protocol/openid-connect/auth`
  + `?client_id=${process.env.REACT_APP_KEYCLOAK_DASHBOARD_CLIENT}`
  + `&redirect_uri=${encodeURIComponent(window.location.href)}`
  + `&response_mode=query`
  + `&response_type=code`
  + `&scope=openid`
  + `&kc_action=`;

export type ProfileFormSecurityProps = {
  user: iUser | null | undefined;
  updateUser: (user:iUserDTO) => void;
  isAdminEdit: boolean;
  isReadOnly: boolean;
}
  export default function ProfileFormSecurity(props: ProfileFormSecurityProps) {
  const [enablePreference, setEnablePreference] = useState<boolean>(false);
  const [mfaPreference, setMfaPreference] = useState<MfaType>();
  const [disableInput, setDisableInput] = useState(false); 
  const [hasInvalidPassword, setHasInvalidPassword] = useState(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const smsMFACodeName = 'mobile-number';
  const authenticatorAppMFACodeName = 'otp';

  type PasswordDialogOptionsType = {
    isOpen: boolean;
    isWorking: boolean;
    newPassword: string | null | undefined;
    confirmPassword: string | null | undefined;
  }
  const [passwordDialogOptions, setPasswordDialogOptions] = useState({} as PasswordDialogOptionsType);

  function handleForget(mfaType : MfaType): void {
    setDisableInput(true);
    let credentialId = props.user?.mfaOptions.find(mfa => mfa.type == (mfaType == MfaType.SMS ? smsMFACodeName : authenticatorAppMFACodeName))?.id;
    if(credentialId != undefined){
      userService.ForgetCredential(props.user?.authId ?? undefined, credentialId, () => { }).then((forceSetup) => {
        if (forceSetup){
          if(!props.isAdminEdit){
            window.location.href = actionUrl+`CONFIGURE_TOTP`;
          }
          else{
            userService.UpdateMfaPreference(props.user?.authId ?? undefined, MfaType.AUTHENTICATOR_APP, () => { }).then(() => {
              userService.GetUser(props.user?.authId ?? undefined, (v) => v)
                .then((result) => {
                  // let user = userService.MapToUser(result);
                  props.updateUser(result);
                });
            }
            ); 
          }
        }
        else{
          userService.GetUser(props.user?.authId ?? undefined, (v) => v)
            .then((result) => {
              // let user = userService.MapToUser(result);
              props.updateUser(result);
            });
        }
      });
    }
    
    setDisableInput(false);
  }
  function handleSetRequiredAction(mfaType : MfaType): void {
    if(props.isAdminEdit){
      userService.SetMFARequiredAction(props.user?.authId ?? undefined, mfaType, () => { }).then(() => {
        userService.GetUser(props.user?.authId ?? undefined, (v) => v)
          .then((result) => {
            // let user = userService.MapToUser(result);
            enqueueSnackbar('Required Action has been created',{variant:'success'})
            props.updateUser(result);
          });
      }
      );
    }
  }

  function handleChangePreference(mfaType : MfaType): void {
    setDisableInput(true);
    userService.UpdateMfaPreference(props.user?.authId ?? undefined, mfaType, () => { }).then(() => {
      userService.GetUser(props.user?.authId ?? undefined, (v) => v)
        .then((result) => {
          // let user = userService.MapToUser(result);
          props.updateUser(result);
          setMfaPreference(mfaType);
        });
    });
    setDisableInput(false);
  }

  useEffect(() => {
    if (props.user) {
      let enablePreference = props.user?.mfaOptions.length == 2
      if (enablePreference) {
        if (props.user?.mfaOptions[0].type == smsMFACodeName) {
          setMfaPreference(MfaType.SMS);
        } else {
          setMfaPreference(MfaType.AUTHENTICATOR_APP);
        }
      }
      setEnablePreference(enablePreference);
    }
  }, [props.user]);

  const handleSetPassword = () => {
    setPasswordDialogOptions({ ...passwordDialogOptions, isOpen: true, isWorking: false });
  };

  useEffect(() =>{
    if (passwordDialogOptions.isWorking) {
      const validationMessages: string[] = [];

      if (!passwordDialogOptions.newPassword) {
        setHasInvalidPassword(true);
        validationMessages.push('Must be set');
      } else {
        const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(passwordDialogOptions.newPassword);
        const hasNumber = /\d/.test(passwordDialogOptions.newPassword);
        const hasUppercase = /[A-Z]/.test(passwordDialogOptions.newPassword);

        if (passwordDialogOptions.newPassword !== passwordDialogOptions.confirmPassword) {
          setHasInvalidPassword(true);
          validationMessages.push('Passwords do not match');
        }

        if (passwordDialogOptions.newPassword?.length < 15) {
          setHasInvalidPassword(true);
          validationMessages.push('Must be at least 15 characters long');
        }

        if (!hasUppercase) {
          setHasInvalidPassword(true);
          validationMessages.push('Must contain an uppercase character');
        }

        if (!hasNumber) {
          setHasInvalidPassword(true);
          validationMessages.push('Must contain a digit');
        }

        if (!hasSpecialChar) {
          setHasInvalidPassword(true);
          validationMessages.push('Must contain a special character');
        }
      }
      
      if (validationMessages.length > 0) {
        setValidationMessages(validationMessages);
        setPasswordDialogOptions({ ...passwordDialogOptions, isOpen: true, isWorking: false});
      } else {
        setHasInvalidPassword(false);
        userService.SetTemporaryPassword(props.user?.authId ?? undefined, passwordDialogOptions.newPassword!, () => {});
        setPasswordDialogOptions({ ...passwordDialogOptions, isOpen: false, isWorking: false});
        enqueueSnackbar('Temporary password has been set.',{variant:'success'});
      }
    }
  }, [passwordDialogOptions])

  return (
    <Container sx={{ p: { xs: 2.25, sm: 4.5 } }}>
      <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 5.5 }}>Password</Typography>

      <TextField
        disabled
        fullWidth
        label='Current Password'
        size='small'
        inputProps={{
          // Attempt to vertically center the stars in the disabled field
          sx: { pt: '17px', pb: '10px' }
        }}
        InputLabelProps={{
          shrink: true,
          sx: {
            color: 'grey.800',
            '&.Mui-disabled': {
              color: 'grey.800'
            }
          }
        }}
        sx={{
          maxWidth: '445px'
        }}
        value='**********'
      />

      <Box sx={{ mt: 1, mb: 4.5 }}>
        {!props.isReadOnly && <Link
          title='Edit password'
          href={!props.isAdminEdit ? actionUrl+`UPDATE_PASSWORD` : undefined}
          onClick={handleSetPassword}
          sx={{
            color: 'var(--link-color)',
            cursor: 'pointer',
            display: 'inline-block',
            fontSize: '0.875rem',
            fontWeight: 600,
            textDecoration: 'none'
          }}
        // onClick={handleEditPassOpen}
        >{!props.isAdminEdit ? 'Edit' : 'Set Temporary Password'}</Link>}
      </Box>

      {/* Admin Edit Password dialog ----------------------------------------- */}
      {passwordDialogOptions.isOpen && <ConfirmationDialog
        ariaPrefix='edit-password-form'
        dialogTitle='Edit Password'
        singleButton={false}
        ctaButtonText='Save'
        ctaButtonWorkingText='Saving...'
        cancelButtonText='Cancel'
        open={passwordDialogOptions.isOpen}
        setOpenDialog={() => setPasswordDialogOptions({ ...passwordDialogOptions, isOpen: false })}
        isWorking={passwordDialogOptions.isWorking}
        setIsWorking={() => setPasswordDialogOptions({ ...passwordDialogOptions, isWorking: true })}
        width={528}
      >
        {hasInvalidPassword &&
          <div>
            <Typography variant="subtitle1" color="error">
              Password requirements:
            </Typography>
            <List>
              {validationMessages.map((msg, index) => (
                <ListItem key={index} sx={{display: 'list-item', pl: 2 }}>
                  <Typography variant="body2" color="error">
                    • {msg}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </div>
        }
        <Typography component='label' htmlFor='new-password-id' sx={visuallyHidden}>New Password</Typography>
        <TextField
          autoFocus
          id='new-password-id'
          placeholder='New password'
          fullWidth
          inputProps={{
            sx: { py: '11px' }
          }}
          size='small'
          value={passwordDialogOptions.newPassword}
          onChange={e => setPasswordDialogOptions({ ...passwordDialogOptions, newPassword: e.target.value })}
          onFocus={e => e.target.select()}
          sx={{ mt: 4.5, mb: -0.5 }}
        />
        <Typography component='label' htmlFor='confirm-password-id' sx={visuallyHidden}>Confirm Password</Typography>
        <TextField
          id='confirm-password-id'
          placeholder='Confirm password'
          fullWidth
          inputProps={{
            sx: { py: '11px' }
          }}
          size='small'
          value={passwordDialogOptions.confirmPassword}
          onChange={e => setPasswordDialogOptions({ ...passwordDialogOptions, confirmPassword: e.target.value })}
          onFocus={e => e.target.select()}
          sx={{ mt: 4.5, mb: -0.5 }}
        />
      </ConfirmationDialog>}

      <Typography variant='h2' sx={{ fontSize: '1.5rem', fontWeight: 500, mb: 2.5 }}>Multi-Factor Authentication</Typography>

      <List>
        <ListItem>
          <ListItemText
            primary='SMS'
            secondary='Text Message'
          />
          {!props.isReadOnly && <> {
            props.user?.mfaOptions.find(mfa => mfa.type === smsMFACodeName) == undefined &&
            <Button
              disabled={disableInput}
              variant='contained'
              size='small'
              sx={{
                bgcolor: 'InceptiaGreen.main',
                fontSize: '1rem',
                px: 5,
                textTransform: 'none',
                marginRight: '35%',
                '&:hover': {
                  backgroundColor: 'InceptiaGreen.dark',
                }
              }}
              onClick={e => {
                if(props.isAdminEdit) {
                  //set required action
                  handleSetRequiredAction(MfaType.SMS);
                }
              }}
              href={!props.isAdminEdit ? actionUrl+`mobile_number_config` : undefined}
            >{!props.isAdminEdit ?  'Setup' : 'Set Required Action'}</Button>
            ||
            <Button
              disabled={disableInput}
              variant='contained'
              size='small'
              sx={{
                bgcolor: 'InceptiaGreen.main',
                fontSize: '1rem',
                px: 5,
                textTransform: 'none',
                marginRight: '35%',
                '&:hover': {
                  backgroundColor: 'InceptiaGreen.dark',
                }
              }}
              onClick={(event)=>{handleForget(MfaType.SMS)}}
            >Forget</Button>
          }
          </>}
        </ListItem>

        <ListItem>
          <ListItemText
            primary='Authenticator App'
            secondary='Duo, Google Authenticator, Authy, etc.'
          />
          {!props.isReadOnly && <> {
            props.user?.mfaOptions.find(mfa => mfa.type === authenticatorAppMFACodeName) == undefined &&
            <Button
              disabled={disableInput}
              variant='contained'
              size='small'
              sx={{
                bgcolor: 'InceptiaGreen.main',
                fontSize: '1rem',
                px: 5,
                textTransform: 'none',
                marginRight: '35%',
                '&:hover': {
                  backgroundColor: 'InceptiaGreen.dark',
                }
              }}
              href={!props.isAdminEdit ? actionUrl+`CONFIGURE_TOTP` : undefined}
              onClick={e => {
                if(props.isAdminEdit) {
                  handleSetRequiredAction(MfaType.AUTHENTICATOR_APP);
                }
              }}
            >{!props.isAdminEdit ?  'Setup' : 'Set Required Action'}</Button>
            ||
            <Button
              disabled={disableInput}
              variant='contained'
              size='small'
              sx={{
                bgcolor: 'InceptiaGreen.main',
                fontSize: '1rem',
                px: 5,
                textTransform: 'none',
                marginRight: '35%',
                '&:hover': {
                  backgroundColor: 'InceptiaGreen.dark',
                }
              }}
              onClick={(event)=>{handleForget(MfaType.AUTHENTICATOR_APP)}}
            >Forget</Button>
          }</>}
        </ListItem>
      </List>

      {
        enablePreference &&
        <>
          <Typography variant='body1' sx={{ fontSize: '1rem', fontWeight: 400, mb: 4.5 }}>
            You have multiple MFA options available. Select your preferred method below.
          </Typography>
          <Grid component="label" container alignItems="center" spacing={1}>
            <Grid item>SMS</Grid>
            <Grid item>
              <Switch
                disabled={disableInput || props.isReadOnly}
                checked={mfaPreference == MfaType.AUTHENTICATOR_APP}
                onChange={(event) => handleChangePreference(event.target.checked ? MfaType.AUTHENTICATOR_APP : MfaType.SMS)}
              />
            </Grid>
            <Grid item>Authenticator App</Grid>
          </Grid>
        </>
      }
    </Container>
  );
}